import { ArtifactStateMark, RowCell, type ArtifactMarkState } from "@don/design-system";
import type { AbsentReason, ArtifactState } from "@don/wire";

import type { Column } from "./columns.js";

/**
 * The Summary column: one of exactly four states, as glyph plus word (design-spec §4
 * "Artifact state", P5, issue 193).
 *
 * **`present` renders nothing**, and that is the fourth treatment rather than a missing one:
 * the header carries the noun, and a column of marks saying the ordinary thing happened makes
 * the three worth noticing harder to notice.
 *
 * Pending is the dotted ring, failed the red triangle (the only state in `--problem`), and an
 * absence by design the barred ring. **A build limit is the same absence with its own word**
 * (issue 85): `Not in build`, so a kind outside the api's derivation set does not claim
 * nobody spoke. No recording has its word too (issue 221). Any other reason, `no_speech` or
 * none, reads the bare `None` (issue 228): a Summary is written from the screen as well as
 * speech, so silence is never why one is absent and the cell does not say it is.
 * `data-absent-reason` carries the wire's spelling of which one.
 *
 * `data-artifact-state` is `wire::ArtifactState`'s own spelling; the design system's mark
 * takes `absent` (issue 190), so this maps.
 */
const MARK: Record<ArtifactState, ArtifactMarkState> = {
  present: "present",
  pending: "pending",
  absent_by_design: "absent",
  failed: "failed",
};

/** The word on the row, and the full sentence its `title` carries. */
function words(
  state: ArtifactState,
  reason: AbsentReason | null,
): { word: string; full: string } | null {
  switch (state) {
    case "present":
      return null;
    case "pending":
      return { word: "Pending", full: "Summary pending" };
    case "failed":
      return { word: "Failed", full: "Summary failed" };
    case "absent_by_design":
      if (reason === "not_in_this_build") {
        return { word: "Not in build", full: "This build writes no summary" };
      }
      if (reason === "no_recording") {
        return { word: "No recording", full: "No recording, so no summary" };
      }
      return { word: "None", full: "No summary for this session" };
  }
}

export function SummaryCell({
  column,
  state,
  reason = null,
}: {
  column: Column;
  state: ArtifactState;
  reason?: AbsentReason | null;
}) {
  const said = words(state, reason);
  return (
    <RowCell
      data-column={column.key}
      data-artifact-state={state}
      data-absent-reason={state === "absent_by_design" && reason !== null ? reason : undefined}
      inset={column.inset}
      overflow="visible"
      title={said?.full}
    >
      {/* The word is the mark's baseline item, so it sits on the row's baseline with the
          other cells' text rather than the glyph's bottom edge doing so. */}
      {said !== null && (
        <ArtifactStateMark state={MARK[state]}>
          <span style={{ alignSelf: "baseline" }}>{said.word}</span>
        </ArtifactStateMark>
      )}
    </RowCell>
  );
}
